'use client';

import { type RefObject } from 'react';

import {
  CircleCheckBig,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import { useTranslations } from 'next-intl';

import { Tooltip } from '@/components/ui/Tooltip';
import {
  AVATAR_TEXT_COLOR,
  getAvatarColor,
  getAvatarInitial,
} from '@/utils/avatarColor';
import {
  formatAbsoluteTime,
  formatRelativeTime,
} from '@/utils/formatRelativeTime';
import { translateDisplayName } from '@/utils/translateDisplayName';

import type { CommentUserInfo } from '@/types/feedback';

interface PinPopoverHeaderProps {
  commenterInfo?: CommentUserInfo;
  createdAt: number;
  commentId?: string;
  isMenuOpen: boolean;
  menuRef: RefObject<HTMLDivElement | null>;
  isResolved?: boolean;
  canDelete: boolean;
  canEdit?: boolean;
  canResolve?: boolean;
  canReopen?: boolean;
  onToggleMenu: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onResolve?: () => void;
  onReopen?: () => void;
  onClose: () => void;
}

export function PinPopoverHeader({
  commenterInfo,
  createdAt,
  commentId,
  isMenuOpen,
  menuRef,
  isResolved,
  canDelete,
  canEdit,
  canResolve,
  canReopen,
  onToggleMenu,
  onEdit,
  onDelete,
  onResolve,
  onReopen,
  onClose,
}: PinPopoverHeaderProps) {
  const t = useTranslations();

  // Resolve lives in its own quick-action button, so the overflow menu is only
  // needed when it would carry a non-resolve action (edit / reopen / delete).
  const hasMenuActions =
    !!canEdit || (!!canReopen && !!isResolved) || canDelete;

  return (
    <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-1">
      <div className="flex items-center gap-2">
        <span
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
          style={{
            backgroundColor: getAvatarColor(commenterInfo?.userName),
            color: AVATAR_TEXT_COLOR,
          }}
        >
          {getAvatarInitial(commenterInfo?.userName)}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-foreground leading-tight">
            {translateDisplayName(commenterInfo?.userName, t)}
          </span>
          <Tooltip content={formatAbsoluteTime(createdAt)}>
            <span className="text-xs text-muted-foreground">
              {formatRelativeTime(createdAt, t)}
            </span>
          </Tooltip>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {canResolve && !isResolved && !!commentId && (
          <button
            onClick={onResolve}
            aria-label={t('get-feedback-pinPopover-resolveComment')}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-green-600 transition-colors"
          >
            <CircleCheckBig className="h-3.5 w-3.5" />
          </button>
        )}
        <div
          ref={menuRef}
          className="relative"
          hidden={!commentId || !hasMenuActions}
        >
          <button
            onClick={onToggleMenu}
            aria-label={t('get-feedback-pinPopover-moreOptions')}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
          {isMenuOpen && (
            <div className="absolute right-0 top-full mt-1 z-10 min-w-[120px] rounded-lg border border-border bg-background shadow-lg py-1">
              {canEdit && (
                <button
                  onClick={onEdit}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors"
                >
                  <Pencil className="h-3 w-3" />
                  {t('get-feedback-pinPopover-edit')}
                </button>
              )}
              {canReopen && isResolved && (
                <button
                  onClick={onReopen}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors"
                >
                  <RotateCcw className="h-3 w-3" />
                  {t('get-feedback-pinPopover-reopen')}
                </button>
              )}
              {canDelete && (
                <button
                  onClick={onDelete}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-red-500 hover:bg-muted transition-colors"
                >
                  <Trash2 className="h-3 w-3" />
                  {t('get-feedback-pinPopover-delete')}
                </button>
              )}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          aria-label={t('get-feedback-pinPopover-close')}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
